import { $, $$, isTablet, sliderConfig } from '@utils';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';
import Splitting from 'splitting';
import Swiper from 'swiper';
import MicroModal from 'micromodal';
import { Navigation, Scrollbar, A11y, FreeMode } from 'swiper/modules';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

const brandLanding = {
  matchesTablet: isTablet.matches,
  wordsColorStart: { color: '#D9E3E6' },
  wordsColorEnd: { color: '#004656', stagger: 0.1 },
  // header selector
  header: '[data-au-bl-header]',
  // sections selectors
  section0: '[data-au-bl-section-0]',
  section1: '[data-au-bl-section-1]',
  section2: '[data-au-bl-section-2]',
  section3: '[data-au-bl-section-3]',
  section4: '[data-au-bl-section-4]',
  section5: '[data-au-bl-section-5]',
  section6: '[data-au-bl-section-6]',
  section7: '[data-au-bl-section-7]',
  section8: '[data-au-bl-section-8]',
  // wrapper selectors
  section1Wrapper: '[data-au-bl-section-1-wrapper]',
  // video selectors
  section0Video: '[data-au-bl-section-0-video]',
  section2Video: '[data-au-bl-section-2-video]',
  section3Video: '[data-au-bl-section-3-video]',
  section4Video: '[data-au-bl-section-4-video]',
  section5Video: '[data-au-bl-section-5-video]',
  // station
  section6Station: '[data-au-bl-section-6-mosaic-item-station]',
  // mask selectors
  section2Mask: '[data-au-bl-section-2-video-mask]',
  section2MaskImg: '[data-au-bl-section-2-video-mask-img]',
  // Texts selectors
  section2Text: '[data-au-bl-section-2-text]',
  // Canvas for image sequence selector
  canvasImageSequence: '[data-au-bl-image-sequence]',
};

const {
  matchesTablet,
  wordsColorStart,
  wordsColorEnd,
  section0,
  section1,
  section2,
  section3,
  section4,
  section5,
  section6,
  section7,
  section8,
  section6Station,
  section1Wrapper,
  section0Video,
  section2Video,
  section3Video,
  section4Video,
  section5Video,
  section2Mask,
  section2MaskImg,
  section2Text,
} = brandLanding;

function init() {
  if (!$('[data-au-bl]')) return;

  gsap.registerPlugin(ScrollTrigger);
  gsap.registerPlugin(ScrollToPlugin);

  auSplitWords();
  auBlinitScrollDirectionIndicator();
  auBldataLayerHeader();
  auBldataLayerFooter();
  auBlmobileMenu();
  auTogglePlayVideo();

  tl0();
  tl1();
  tl2();
  tl3();
  tl4();
  tl5();
  tl6();
  tl7();
  tl8();
  tl9();
  tl10();
  tl11();
}

function tl0() {
  const tl0 = gsap.timeline({
    scrollTrigger: {
      trigger: section0,
      end: '+=25%',
      scrub: true,
      pin: true,
    },
  });

  tl0.add('tl0').to(section0Video, { scale: 1.5 }, 'tl0').to(section1, { yPercent: -50, opacity: 1 }, 'tl0');
}

function tl1() {
  const tl1 = gsap.timeline({
    scrollTrigger: {
      trigger: section1,
      end: '-=100%',
      scrub: true,
    },
  });

  tl1.fromTo(wordsSelection(section1), wordsColorStart, wordsColorEnd);
}

function tl2() {
  const tl2 = gsap.timeline({
    scrollTrigger: {
      trigger: section1,
      start: '-50% top',
      end: '+=50%',
      scrub: true,
      pin: true,
      pinSpacing: false,
    },
  });

  tl2.fromTo(section1Wrapper, { opacity: 1 }, { opacity: 0 });
}

function tl3() {
  const tl3 = gsap.timeline({
    scrollTrigger: {
      trigger: section2Video,
      start: 'top 100%',
      end: '-=10%',
      scrub: true,
      onUpdate: ({ progress }) => {
        if (progress > 0.5) {
          $(section2Video).play();
        }
      },
    },
  });

  tl3.to(section2Video, { scale: 1 });
  tl3.to(section2Video, { borderRadius: 0, duration: 0.25 });
}

function tl4() {
  const tl4 = gsap.timeline({
    scrollTrigger: {
      trigger: section2,
      start: 'top top',
      end: '+=175%',
      pin: true,
      scrub: true,
    },
  });

  tl4
    .add('tl4')
    .fromTo(
      section2MaskImg,
      { transform: 'matrix(80, 0, 0, 80, 0, 0)', left: 0 },
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        left: matchesTablet ? 0 : 284,
        top: matchesTablet ? -120 : 0,
        duration: 4,
      },
      matchesTablet ? 0 : 2, // delay
      'tl4',
    )
    .fromTo(
      section2Mask,
      { transform: 'matrix(80, 0, 0, 80, 0, 0)', left: 0 },
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        left: matchesTablet ? 0 : 284,
        top: matchesTablet ? -120 : 0,
        duration: 4,
      },
      matchesTablet ? 0 : 2, // delay
      'tl4',
    )
    .to(
      section2MaskImg,
      {
        backgroundColor: '#0478b2',
        duration: 4,
      },
      matchesTablet ? 0 : 3, // delay
      'tl4',
    )
    .fromTo(
      section2Text,
      { opacity: 0, yPercent: 200 },
      { opacity: 1, yPercent: matchesTablet ? 20 : 10, duration: 1 },
      matchesTablet ? 3 : 5, // delay
      'tl4',
    );
}

function tl5() {
  const tl5 = gsap.timeline({
    scrollTrigger: {
      trigger: section3,
      start: 'top-=50% top',
      end: 'bottom-=90%',
      scrub: true,
    },
  });

  tl5
    .add('tl5')
    .fromTo(section2, { opacity: 1 }, { opacity: 0, duration: 10 }, 'tl5')
    .fromTo(section3, { opacity: 0 }, { opacity: 1 }, 'tl5')
    .fromTo(wordsSelection(section3), wordsColorStart, wordsColorEnd, 5, 'tl5');
}

function tl6() {
  const tl6 = gsap.timeline({
    scrollTrigger: {
      trigger: section3,
      start: 'top top',
      end: '+=100%',
      pin: true,
      scrub: true,
      onUpdate: ({ progress }) => {
        if (progress > 0.5) {
          $(section3Video).play();
        }

        if (progress > 0.9) {
          $(section4Video).play();
        }
      },
    },
  });

  tl6
    .add('tl6')
    .fromTo(
      '[data-au-bl-section-3-title]',
      { transformPerspective: 800, rotateY: '0' },
      { transformPerspective: 400, rotateY: matchesTablet ? 0 : '70deg', opacity: 0, duration: 30 },
      10,
      'tl6',
    )
    .fromTo(
      '[data-au-bl-section-3-description]',
      { transformPerspective: 800, rotateY: '0' },
      { transformPerspective: 400, rotateY: matchesTablet ? 0 : '-70deg', opacity: 0, duration: 30 },
      10,
      'tl6',
    )
    .fromTo(
      '[data-au-bl-section-3-wrapper]',
      { transformPerspective: 800, rotateY: '0' },
      { transformPerspective: 400, rotateY: matchesTablet ? '70deg' : 0, opacity: 0, duration: 30 },
      matchesTablet ? 0 : 10,
      'tl6',
    )
    .to('[data-au-bl-section-3-video-container]', { scale: 1, duration: 45 }, 10, 'tl6')
    .to('[data-au-bl-section-3-video-container]', { borderRadius: 0, duration: 0.5 });
}

function tl7() {
  const tl7 = gsap.timeline({
    scrollTrigger: {
      trigger: section4,
      start: 'top top',
      end: '+150%',
      pin: true,
      scrub: true,
      onUpdate: ({ isActive }) => {
        if (isActive) {
          $(section4Video).play();
        }
      },
    },
  });

  tl7
    .add('tl7')
    .fromTo('[data-au-bl-section-4-title]', { opacity: 1 }, { opacity: 0, duration: 10 }, 18, 'tl7')
    .fromTo('[data-au-bl-section-4-title]', { y: 0 }, { y: -100, duration: 10 }, 18, 'tl7');
}

function tl8() {
  const tl8 = gsap.timeline({
    scrollTrigger: {
      trigger: section5,
      start: 'top top',
      end: '+=200%',
      pin: true,
      scrub: true,
      onUpdate: ({ isActive }) => {
        if (isActive) {
          $(section5Video).play();
        }
      },
    },
  });

  tl8
    .add('tl8')
    .fromTo('[data-au-bl-section-5-title]', { opacity: 1 }, { opacity: 0 }, 'tl8')
    .fromTo('[data-au-bl-section-5-title]', { yPercent: 0 }, { yPercent: -50 }, 'tl8')
    .fromTo(section5Video, { yPercent: 100 }, { yPercent: 0 }, 'tl8')
    .to(section5Video, { scale: 1 })
    .to(section5Video, { borderRadius: 0, duration: 0.25 });
}

function tl9() {
  const tl9 = gsap.timeline({
    scrollTrigger: {
      trigger: section6,
      start: 'top top',
      end: '1400%',
      pin: true,
      scrub: true,
      onUpdate: ({ progress, isActive }) => {
        if (isActive) {
          toggleCarouselActiveElements(progress);
        }
      },
    },
  });

  const refStart = $('[data-au-bl-section-6-item-1]');
  const refEnd = $('[data-au-bl-section-6-mosaic-item-ref]');
  const mediaHeightStart = matchesTablet
    ? $('[data-au-bl-section-6-carousel]').offsetWidth * (9 / 16)
    : refStart.offsetHeight;
  const mediaWidthEnd = refEnd.offsetWidth;
  const mediaHeightEnd = refEnd.offsetHeight;
  const stationEndlWidth = (1440 * window.innerHeight) / 678;
  let stationFinalWidth, stationFinalHeight;

  if (stationEndlWidth > window.innerWidth) {
    stationFinalWidth = window.innerWidth - (matchesTablet ? 32 : 64);
    stationFinalHeight = stationFinalWidth * (678 / 1440);
  } else {
    stationFinalHeight = window.innerHeight - (matchesTablet ? 32 : 152);
    stationFinalWidth = stationFinalHeight * (1440 / 678);
  }

  const containerTransformOnEndCarousel = '[data-au-bl-section-6-item-container-6]';
  const videoTransformOnEndCarousel = '[data-au-bl-section-6-item-6]';

  tl9
    .add('tl9-0')
    .fromTo('[data-au-bl-section-6-item-container-1]', { xPercent: 100, opacity: 1 }, { xPercent: 0 }, 'tl9-0')
    .fromTo('[data-au-bl-section-6-item-container-2]', { opacity: 0 }, { opacity: 1 }, 'tl9-0')

    .add('tl9-1')
    .fromTo('[data-au-bl-section-6-item-container-1]', { scale: 1 }, { scale: 0 }, 'tl9-1')
    .fromTo('[data-au-bl-section-6-item-container-2]', { xPercent: 200 }, { xPercent: 0 }, 'tl9-1')
    .fromTo('[data-au-bl-section-6-item-container-3]', { opacity: 0 }, { opacity: 1 }, 'tl9-1')

    .add('tl9-2')
    .fromTo('[data-au-bl-section-6-item-container-2]', { scale: 1 }, { scale: 0 }, 'tl9-2')
    .fromTo('[data-au-bl-section-6-item-container-3]', { xPercent: 200 }, { xPercent: 0 }, 'tl9-2')
    .fromTo('[data-au-bl-section-6-item-container-4]', { opacity: 0 }, { opacity: 1 }, 'tl9-2')

    .add('tl9-2a')
    .fromTo('[data-au-bl-section-6-item-container-3]', { scale: 1 }, { scale: 0 }, 'tl9-2a')
    .fromTo('[data-au-bl-section-6-item-container-4]', { xPercent: 200 }, { xPercent: 0 }, 'tl9-2a')
    .fromTo('[data-au-bl-section-6-item-container-5]', { opacity: 0 }, { opacity: 1 }, 'tl9-2a')

    .add('tl9-2b')
    .fromTo('[data-au-bl-section-6-item-container-4]', { scale: 1 }, { scale: 0 }, 'tl9-2b')
    .fromTo('[data-au-bl-section-6-item-container-5]', { xPercent: 200 }, { xPercent: 0 }, 'tl9-2b')
    .fromTo('[data-au-bl-section-6-item-container-6]', { opacity: 0 }, { opacity: 1 }, 'tl9-2b')

    .add('tl9-3')
    .fromTo('[data-au-bl-section-6-item-container-5]', { scale: 1 }, { scale: 0 }, 'tl9-3')
    .fromTo('[data-au-bl-section-6-item-container-6]', { xPercent: 200 }, { xPercent: 0 }, 'tl9-3')

    .add('tl9-4')
    .fromTo('[data-au-bl-section-6-nav]', { opacity: 1 }, { opacity: 0 }, 'tl9-4')
    .to(
      containerTransformOnEndCarousel,
      {
        width: mediaWidthEnd,
        height: mediaHeightEnd,
        top: matchesTablet ? 'calc(50% + 4px)' : 0,
        left: matchesTablet ? 16 : 0,
        right: matchesTablet ? 16 : 0,
      },
      'tl9-4',
    )
    .fromTo(
      videoTransformOnEndCarousel,
      {
        height: mediaHeightStart,
      },
      {
        height: mediaHeightEnd,
      },
      'tl9-4',
    )
    .fromTo('[data-au-bl-play-video]', { opacity: 1 }, { opacity: 0 }, 'tl9-4')
    .fromTo(
      '[data-au-bl-section-6-mosaic-item]',
      { scale: 0 },
      {
        scale: 1,
        onStart: () => {
          $$('[data-au-bl-section-6-mosaic-item] video').forEach((video) => {
            video.play();
          });
        },
      },
      'tl9-4',
    );
  if (matchesTablet) {
    tl9.fromTo('[data-au-bl-section-6-mosaic-item-5]', { scale: 0 }, { scale: 1 }, 'tl9-4');
    tl9.to('[data-au-bl-play-video]', { pointerEvents: 'none' }, 'tl9-4');
  }

  tl9
    .add('tl9-5')
    .to('[data-au-bl-play-video]', { pointerEvents: 'none' }, 'tl9-5')
    .fromTo(
      `[data-au-bl-section-6-mosaic-item]:not(${section6Station}), ${containerTransformOnEndCarousel}`,
      { opacity: 1 },
      { opacity: 0 },
      'tl9-5',
    )
    .fromTo('[data-au-bl-section-6-station-title]', { opacity: 0 }, { opacity: 1 }, 'tl9-5')
    .add('tl9-6')
    .to(
      '[data-au-bl-section-6-station-title]',
      { transform: matchesTablet ? 'translate(0, 0)' : 'translate(50px, 50px)' },
      'tl9-6',
    )
    .fromTo('[data-au-bl-section-6-station-title]', { opacity: 1 }, { opacity: matchesTablet ? 1 : 0 }, 'tl9-6')
    .to(section6Station, { position: 'fixed' }, 'tl9-6')
    .fromTo(section6Station, { top: $(section6Station).offsetTop }, { top: matchesTablet ? 0 : '50%' }, 'tl9-6')
    .to(section6Station, { borderRadius: matchesTablet ? 0 : 16 }, 'tl9-6')
    .fromTo(section6Station, { left: $(section6Station).offsetLeft }, { left: '50%' }, 'tl9-6')
    .to(section6Station, { transform: matchesTablet ? 'translate(-50%, 0)' : 'translate(-50%, -50%)' }, 'tl9-6')
    .add('tl9-7')
    .fromTo(
      '[data-au-bl-section-6-mosaic-item-station-init]',
      { opacity: 1 },
      { opacity: 0 },
      matchesTablet ? 'tl9-6' : 'tl9-7',
    )
    .fromTo(
      '[data-au-bl-section-6-mosaic-item-station-end]',
      { opacity: 0 },
      { opacity: 1 },
      matchesTablet ? 'tl9-6' : 'tl9-7',
    )
    .fromTo(
      section6Station,
      { height: $(section6Station).offsetHeight },
      { height: stationFinalHeight },
      matchesTablet ? 'tl9-6' : 'tl9-7',
    )
    .fromTo(
      section6Station,
      { width: $(section6Station).offsetWidth },
      { width: matchesTablet ? '100vw' : stationFinalWidth },
      matchesTablet ? 'tl9-6' : 'tl9-7',
    )
    .fromTo('[data-au-bl-section-6-station-items]', { yPercent: matchesTablet ? 150 : 0 }, { yPercent: 0 }, 'tl9-6')
    .add('tl9-8')
    .to('[data-au-bl-section-6-mosaic]', { pointerEvents: matchesTablet ? 'none' : 'all' }, 'tl9-8')
    .to(
      '[data-au-bl-section-6-station-items]',
      { pointerEvents: matchesTablet ? 'all' : 'none' },
      matchesTablet ? 'tl9-7' : 'tl9-8',
    )
    .fromTo('[data-au-bl-section-6-hotspots]', { opacity: 0 }, { opacity: 1 }, 'tl9-8');

  $$('[data-au-bl-section-6-nav-list-button]').forEach((button) => {
    button.addEventListener('click', (e) => {
      const li = e.target.closest('li');
      const nodes = Array.from(li.closest('ul').children);
      const index = nodes.indexOf(li) + 1;
      const clickText = removeAccentMarkAndUppercase(button.innerText);
      let progressTarget;

      if (index === 1) {
        progressTarget = 0.089;
      }

      if (index === 2) {
        progressTarget = 0.171;
      }

      if (index === 3) {
        progressTarget = 0.27;
      }

      if (index === 4) {
        progressTarget = 0.365;
      }

      if (index === 5) {
        progressTarget = 0.453;
      }

      if (index === 6) {
        progressTarget = 0.542;
      }

      gsap.to(window, {
        scrollTo: () => getScrollPosition(tl9, progressTarget),
      });

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickEnlace',
        item_id: `indice_${clickText}`,
      });
    });
  });

  // service station modal
  $$('[data-au-bl-section-6-mosaic-item-station] a').forEach((el) => {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      const modalId = e.currentTarget.getAttribute('data-modal-trigger');

      MicroModal.show(modalId, {
        onShow: () => {
          document.body.classList.add('au-overflow-hidden');
          $('[data-au-bl-section-6-mosaic]').style.pointerEvents = 'none';
          $('[data-au-bl-section-6-station-items]').style.pointerEvents = 'all';

          window.dataLayer.push({
            event: 'navigation',
            content_type: 'clickBoton',
            item_id: 'clickHotspot',
          });
        },
        onClose: () => {
          document.body.classList.remove('au-overflow-hidden');
          $('[data-au-bl-section-6-mosaic]').style.pointerEvents = 'all';
          $('[data-au-bl-section-6-station-items]').style.pointerEvents = 'none';

          window.dataLayer.push({
            event: 'navigation',
            content_type: 'clickBoton',
            item_id: 'clickHotspot_cerrar',
          });
        },
      });
    });
  });

  // mobile slider
  if (matchesTablet) {
    $$('[data-au-bl-section-6-station-items-slider]').forEach((slider) => {
      new Swiper(slider, {
        modules: [Navigation, Scrollbar, A11y, FreeMode],
        ...sliderConfig,
        slidesPerView: 1.2,
        spaceBetween: 8,
      });
    });
  }
}

function tl10() {
  const windowWidth = window.innerWidth;
  let titleScale;
  if (windowWidth < 1260 && windowWidth >= 1024) {
    titleScale = 1.75;
  } else if (windowWidth < 1024) {
    titleScale = 1;
  } else {
    titleScale = 2.25;
  }
  const tl10 = gsap.timeline({
    scrollTrigger: {
      trigger: section7,
      start: matchesTablet ? 'top-=100% top' : 'top-=50% top',
      end: '+=1200%',
      pin: true,
      scrub: true,
      onUpdate: ({ isActive }) => {
        const section7videos = $$('[data-au-bl-section-7-video]');

        if (isActive) {
          section7videos.forEach((video) => {
            video.play();
          });
        }
      },
    },
  });

  tl10
    .add('tl10')
    .to(
      '[data-au-bl-section-7-wrapper-1]',
      {
        clipPath: 'polygon(0% 100%,100% 100%,100% 0%,0% 0%)',
        duration: 1,
      },
      'tl10',
    )
    .add('tl10-1')
    .fromTo(
      '[data-au-bl-section-7-wrapper-1-title]',
      { scale: titleScale },
      { scale: 1, duration: matchesTablet ? 0 : 1 },
      'tl10-1',
    )
    .add('tl10-2')
    .to(
      '[data-au-bl-section-7-wrapper-2]',
      { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%,0% 100%)', duration: 1 },
      'tl10-2',
    )
    .fromTo(
      '[data-au-bl-section-7-wrapper-2-pic-group-1]',
      { yPercent: 100 },
      {
        yPercent: -100,
        duration: 2,
      },
      matchesTablet ? 1.4 : 2.4,
      'tl10-2',
    )
    .to(
      '[data-au-bl-section-7-wrapper-3]',
      { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%,0% 100%)' },
      matchesTablet ? 3.1 : 4.1,
      'tl10-2',
    )
    .fromTo(
      '[data-au-bl-section-7-wrapper-3-pic-group-1]',
      { yPercent: 100 },
      { yPercent: -100, duration: 2 },
      matchesTablet ? 3.3 : 4.3,
      'tl10-2',
    )
    .to(
      '[data-au-bl-section-7-wrapper-4]',
      { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%,0% 100%)' },
      matchesTablet ? 5 : 6,
      'tl10-2',
    )
    .fromTo(
      '[data-au-bl-section-7-wrapper-4-pic-group-1]',
      { yPercent: 100 },
      { yPercent: -100, duration: 2 },
      matchesTablet ? 5.2 : 6.2,
      'tl10-2',
    )
    .to(
      '[data-au-bl-section-7-wrapper-5]',
      { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%,0% 100%)' },
      matchesTablet ? 6.9 : 7.9,
      'tl10-2',
    )
    .fromTo(
      '[data-au-bl-section-7-wrapper-5-pic-group-1]',
      { yPercent: 100 },
      { yPercent: -100, duration: 2 },
      matchesTablet ? 7.1 : 8.1,
      'tl10-2',
    )
    .fromTo(
      '[data-au-bl-section-8]',
      { yPercent: 0 },
      { yPercent: matchesTablet ? -200 : -150, duration: 2 },
      matchesTablet ? 8 : 9,
      'tl10-2',
    );
}

function tl11() {
  const galleryWidth = $('[data-au-bl-section-8-gallery-top]').scrollWidth;

  const tl11 = gsap.timeline({
    scrollTrigger: {
      trigger: section8,
      start: matchesTablet ? 'top-=200% top' : 'top-=150% top',
      end: '+=1000%',
      pin: true,
      scrub: true,
      onUpdate: ({ progress }) => {
        if (progress > 0.7) {
          $('[data-au-bl-section-9]').classList.add('au-bl__section-9--visible');
          $('[data-au-bl-section-8-gallery-bottom]').classList.add('au-bl__section-8-gallery--hidden');
          $('[data-au-bl-section-8-gallery-top]').classList.add('au-bl__section-8-gallery--hidden');
        } else {
          $('[data-au-bl-section-9]').classList.remove('au-bl__section-9--visible');
          $('[data-au-bl-section-8-gallery-bottom]').classList.remove('au-bl__section-8-gallery--hidden');
          $('[data-au-bl-section-8-gallery-top]').classList.remove('au-bl__section-8-gallery--hidden');
        }
      },
    },
  });

  tl11
    .add('tl11')
    .fromTo('[data-au-bl-section-8-title]', { opacity: 1 }, { opacity: 0, duration: 0.5 }, 'tl11')
    .fromTo(
      '[data-au-bl-section-8-gallery-top]',
      { x: `${window.innerWidth}px` },
      { x: `-${galleryWidth}px`, duration: 3 },
      'tl11',
    )
    .fromTo(
      '[data-au-bl-section-8-gallery-bottom]',
      { x: `-${galleryWidth}px` },
      { x: `${window.innerWidth}px`, duration: 3 },
      'tl11',
    );
}

/**
 * function to get scroll position of concrete timeline and scroll to concrete progress inside of it
 *  @param timeline
 *  @param progress
 */
function getScrollPosition(timeline, progress) {
  const p = gsap.utils.clamp(0, 1, progress || 0),
    nested = !timeline.scrollTrigger,
    st = nested ? timeline.parent.scrollTrigger : timeline.scrollTrigger,
    containerAnimation = st.vars.containerAnimation,
    range = st.end - st.start,
    position = st.start + range * p;
  if (containerAnimation) {
    const st = containerAnimation.scrollTrigger;
    return st.start + (st.end - st.start) * (position / containerAnimation.duration());
  }
  if (nested) {
    const start = st.start + (timeline.startTime() / timeline.parent.duration()) * range,
      end = st.start + ((timeline.startTime() + timeline.duration()) / timeline.parent.duration()) * range;
    return start + (end - start) * p;
  }
  return position;
}

/**
 * function to split all words inside data-au-split-words selector, using splitting plugin https://splitting.js.org/guide.html
 */
function auSplitWords() {
  const target = document.querySelectorAll('[data-au-split-words]'),
    // eslint-disable-next-line new-cap, no-unused-vars
    results = Splitting({ target, by: 'words' });
}

/**
 * function to select all splitted words with word class (default class of splitting plugin. Cannot be modified https://splitting.js.org/guide.html#words)
 * @param target
 */
function wordsSelection(target) {
  return $(target).querySelectorAll('[data-au-split-words] .word');
}

/**
 * function to toggle active elements inside section 6
 * @param progress
 */
function toggleCarouselActiveElements(progress) {
  let index;

  if (progress < 0.6) {
    if (progress > 0) {
      index = 1;
    }
    if (progress > 0.13) {
      index = 2;
    }
    if (progress > 0.22) {
      index = 3;
    }
    if (progress > 0.31) {
      index = 4;
    }
    if (progress > 0.402) {
      index = 5;
    }
    if (progress > 0.493) {
      index = 6;
    }

    $$('[data-au-bl-section-6-nav-list-button]').forEach((button) => {
      button.classList.remove('au-bl__section-6-nav-list-button--active');
      button.classList.remove('t-h3');
      button.classList.remove('t-label');
    });

    const activeButton = $(
      `[data-au-bl-section-6-nav-list-li]:nth-child(${index}) [data-au-bl-section-6-nav-list-button]`,
    );
    const isVideoMuted = $(`[data-au-bl-section-6-item-${index}]`).getAttribute('muted');

    if (isVideoMuted) $(`[data-au-bl-section-6-item-${index}]`).play();
    activeButton.classList.add('au-bl__section-6-nav-list-button--active');
    activeButton.classList.add('t-h3');
    activeButton.classList.remove('t-label');
  }
}

function auBlinitScrollDirectionIndicator() {
  let lastScrollTop = 0;

  window.addEventListener('scroll', function () {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop > lastScrollTop) {
      // Scroll down
      document.body.classList.add('au-bl__scrolling-down');
      document.body.classList.remove('au-bl__scrolling-up');
    } else if (scrollTop > 0) {
      // Scroll up
      document.body.classList.add('au-bl__scrolling-up');
      document.body.classList.remove('au-bl__scrolling-down');
    } else {
      document.body.classList.remove('au-bl__scrolling-up');
      document.body.classList.remove('au-bl__scrolling-down');
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // To avoid negative values on iOS
  });
}

function auBldataLayerFooter() {
  $('[data-au-bl-section-9-mailto]').addEventListener('click', (e) => {
    const clickText = removeAccentMarkAndUppercase(e.target.innerText);

    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickEmail',
      item_id: `footer_${clickText}`,
    });
  });
}

function auBldataLayerHeader() {
  $$('[data-au-bl-header-logo]').forEach((logo) => {
    logo.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(e.currentTarget.innerText);

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu superior_logo',
        item_id,
      });
    });
  });

  $$('[data-au-bl-header-collasable]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(
        e.currentTarget.querySelector('[data-au-collapsable-title-text]').innerText,
      );

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu superior_utilities',
        item_id,
      });
    });
  });

  $$('[data-au-bl-header-link]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(e.currentTarget.innerText);

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu superior_utilities',
        item_id,
      });
    });
  });

  $$('[data-au-bl-collapsable-close]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const title = removeAccentMarkAndUppercase(
        e.currentTarget.closest('[data-au-collapsable-parent]').querySelector('[data-au-collapsable-title-text]')
          .innerText,
      );

      e.currentTarget
        .closest('[data-au-collapsable-parent]')
        .querySelector('[data-au-collapsable] [data-au-collapsable-icon]').innerText = 'expand_more';

      const item_id = `${title}_cerrar`;

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu superior_utilities',
        item_id,
      });
    });
  });

  $$('[data-au-bl-menu-link]').forEach((el) => {
    el.addEventListener('click', (e) => {
      let titleText;

      if (e.target.closest('[data-au-collapsable-parent]')) {
        titleText = e.target
          .closest('[data-au-collapsable-parent]')
          .querySelector('[data-au-collapsable-title-text]').innerText;
      }

      if (e.target.closest('[data-au-bl-menu-links]')) {
        titleText = e.target
          .closest('[data-au-bl-menu-links]')
          .querySelector('[data-au-bl-menu-links-title]').innerText;
      }

      const clickText = e.target.innerText;
      const item_id = `${removeAccentMarkAndUppercase(titleText)}_${removeAccentMarkAndUppercase(clickText)}`;

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu superior_utilities',
        item_id,
      });
    });
  });

  $$('[data-au-bl-menu-legal-link]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(e.target.innerText);

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickEnlace',
        item_id,
      });
    });
  });

  $$('[data-au-bl-footer-legal-link]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(e.target.innerText);

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'footer',
        item_id,
      });
    });
  });

  $$('[data-au-bl-rrss-link]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = removeAccentMarkAndUppercase(e.currentTarget.innerText);

      window.dataLayer.push({
        event: 'select_content',
        content_type: 'menu_rrss',
        item_id,
      });
    });
  });
}

function auBlmobileMenu() {
  $$('[data-au-bl-menu-trigger]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const item_id = e.currentTarget.dataset.auBlMenuTrigger === 'open' ? 'abrir' : 'cerrar';

      document.body.classList.toggle('au-bl__menu-open');
      document.body.classList.toggle('au-overflow-hidden');

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'menu superior_hamburguesa',
        item_id,
      });
    });
  });
}

function auTogglePlayVideo() {
  $('[data-au-bl-play-video]').addEventListener('click', (e) => {
    const button = e.target;
    const video = button.parentNode.querySelector('[data-au-toggle-video]');
    button.classList.add('au-hide');

    video.addEventListener('ended', () => {
      button.classList.remove('au-hide');
    });

    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickVideo',
      item_id: `${removeAccentMarkAndUppercase(button.getAttribute('title'))}_${removeAccentMarkAndUppercase(button.dataset.auPlayText)}`,
    });

    video.play();
  });
}

export default { init };
